import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { chatService } from '../services/chatService';
import { useAuth } from '../contexts/AuthContext';
import UnauthenticatedMessage from '../components/UnauthenticatedMessage';
import { eventService } from '../services/eventService';
import { ChatMessage } from '../components/chat/ChatMessage';
import { ChatInput } from '../components/chat/ChatInput';
import { ChatLoadingIndicator } from '../components/chat/ChatLoadingIndicator';
import { canSendChatMessage, OptimisticMessage, Message, visibleMessages } from '../types/chat';
import { useTitle } from '../contexts/TitleContext';

const Chat: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { userInfo, loading: authLoading } = useAuth();
  const { title, setTitle } = useTitle();
  
  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [optimisticMessages, setOptimisticMessages] = useState<OptimisticMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingHistory, setIsFetchingHistory] = useState(true);
  const [chatStatus, setChatStatus] = useState<string>('finish');
  const [agentType, setAgentType] = useState<string | null>(null);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastHistoryIdRef = useRef<number>(0);
  const isInitialLoad = useRef<boolean>(true);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchChatHistory = useCallback(async () => {
    if (!id) return;
    try {
      if (isInitialLoad.current) setIsFetchingHistory(true);

      const response = await chatService.fetchChatHistory(
        parseInt(id),
        isInitialLoad.current ? undefined : lastHistoryIdRef.current,
      );

      if (response.status !== 'pending' && response.status !== 'processing' && pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setChatStatus(response.status);
      setAgentType(response.agent || null);
      if (response.last_history_id) lastHistoryIdRef.current = response.last_history_id;

      if (isInitialLoad.current) {
        setMessages(response.history);
        window.setTimeout(scrollToBottom, 100);
        const firstUserMessage = response.history.find((message: Message) => message.type === 'user');
        if (firstUserMessage && 'content' in firstUserMessage) {
          setTitle(firstUserMessage.content.length > 50 ? `${firstUserMessage.content.substring(0, 50)}...` : firstUserMessage.content);
        }
        isInitialLoad.current = false;
      } else {
        setMessages((current) => [...current, ...response.history]);
        if (response.history.length > 0) {
          setOptimisticMessages([]);
          response.history.forEach((message: Message) => {
            if (message.type === 'agent' && message.id) {
              void eventService.captureAgentResponse(parseInt(id), Number(message.id), response.agent);
            }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setIsFetchingHistory(false);
    }
  }, [id, scrollToBottom, setTitle]);

  useEffect(() => {
    if (id) {
      fetchChatHistory();
    }
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchChatHistory, id]);

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (chatStatus === 'pending' || chatStatus === 'processing') {
      pollingIntervalRef.current = setInterval(() => {
        fetchChatHistory();
      }, 2000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [chatStatus, fetchChatHistory, id]);

  // Event handlers
  const handleProductClick = (productId: number) => {
    void eventService.captureProductClick(Number(id), productId, 'pdp', agentType);
  };

  // Send message on behalf of user (for "show more" buttons in categorized products)
  const handleSendMessage = async (messageContent: string) => {
    if (!id || isLoading || !canSendChatMessage(chatStatus)) return;
    
    // Create optimistic message
    const optimisticMessage: OptimisticMessage = {
      id: `optimistic-${Date.now()}`,
      type: 'user',
      content: messageContent,
      created_at: new Date().toISOString(),
      isSending: true
    };
    
    // Add optimistic message immediately
    setOptimisticMessages([optimisticMessage]);
    setIsLoading(true);
    
    // Scroll to bottom when user sends a message
    setTimeout(() => scrollToBottom(), 100);

    try {
      await chatService.sendMessage(parseInt(id), messageContent);
      await fetchChatHistory();
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error sending message:', error);
      setIsLoading(false);
      setOptimisticMessages([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading && id && canSendChatMessage(chatStatus)) {
      const messageContent = inputValue;
      
      // Create optimistic message
      const optimisticMessage: OptimisticMessage = {
        id: `optimistic-${Date.now()}`,
        type: 'user',
        content: messageContent,
        created_at: new Date().toISOString(),
        isSending: true
      };
      
      // Add optimistic message immediately
      setOptimisticMessages([optimisticMessage]);
      setInputValue('');
      setIsLoading(true);
      
      // Scroll to bottom when user sends a message
      setTimeout(() => scrollToBottom(), 100);

      try {
        void eventService.captureSubmitMessage(parseInt(id), messageContent, agentType);

        // Send message to API
        await chatService.sendMessage(parseInt(id), messageContent);

        // Fetch updated history once - this will update the status and trigger polling if needed
        await fetchChatHistory();
        setIsLoading(false);
      } catch (error: any) {
        console.error('Error sending message:', error);
        setIsLoading(false);
        // Remove the optimistic message on error
        setOptimisticMessages([]);
        const errorMsg = error.response?.data?.detail || 'Failed to send message';
        alert(errorMsg);
      }
    }
  };


  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center text-gray-600 dark:text-gray-400">
          در حال بررسی احراز هویت...
        </div>
      </div>
    );
  }

  // Show unauthenticated message if user is not logged in
  if (!userInfo?.is_authenticated) {
    return <UnauthenticatedMessage />;
  }

  // Combine messages with optimistic messages for rendering
  const isProcessing = chatStatus === 'pending' || chatStatus === 'processing';
  const allMessages = visibleMessages([...messages, ...optimisticMessages], isProcessing);

  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-ds-canvas">
      <div className="flex h-[54px] shrink-0 items-center gap-2.5 border-b border-ds-100 bg-ds-white px-3 app-sm:h-[58px] app-sm:px-5">
        <div className="min-w-0">
          <h1 className="truncate text-sm font-bold text-ds-ink">{title || 'گفت‌وگو با دستیار'}</h1>
          <small className="mt-0.5 block text-[10px] text-ds-500">دستیار خرید باسلام</small>
        </div>
        <span className="mr-auto hidden items-center gap-1.5 text-[11px] text-ds-success app-sm:flex">
          <i className="h-1.5 w-1.5 rounded-full bg-ds-success" />
          {isProcessing ? 'در حال پاسخ‌گویی...' : 'آماده پاسخ‌گویی'}
        </span>
      </div>

      <div className="ds-scrollbar min-h-0 flex-1 overflow-y-auto px-3 pb-[126px] pt-[18px] app-sm:px-6 app-sm:pt-[26px]">
        <div className="mx-auto max-w-[920px]">
          {isFetchingHistory ? (
            <div className="grid min-h-48 place-items-center text-xs text-ds-500">
              <div className="flex items-center gap-2"><span className="h-2 w-2 animate-pulse rounded-full bg-ds-primary" />در حال بارگذاری گفت‌وگو...</div>
            </div>
          ) : (
            <>
              {allMessages.map((message, index) => {
                // Check if this is the last agent message (no user messages after it)
                const isLastAgentMessage = message.type === 'agent' && 
                  !allMessages.slice(index + 1).some(m => m.type === 'user');
                return (
                  <ChatMessage 
                    key={message.id}
                    message={message}
                    onProductClick={handleProductClick}
                    onSendMessage={handleSendMessage}
                    isLastAgentMessage={isLastAgentMessage}
                    agent_chat_id={id ? parseInt(id) : undefined}
                  />
                );
              })}
              {isProcessing && <ChatLoadingIndicator />}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>
      
      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        chatStatus={chatStatus}
      />
    </div>
  );
};

// The desktop sidebar moves from one chat to another on the same route, so React kept this page mounted:
// it showed the first chat's messages and asked only for history after that chat's last id, and the chat
// picked in the sidebar never appeared. A fresh mount for every chat id starts each one from its own history.
const ChatPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Chat key={id} />;
};

export default ChatPage;
